import { Switch, notification } from 'antd'
import React, { forwardRef, useEffect, useState } from 'react'
import ApiClient from '@quiqupltd/quiqupjs/lib/network/api-client'
import { useAnalytics } from '../../hooks/useAnalytics'
import { useEnvVars } from '../../hooks/useEnvVars'
import { EventMapper } from './utils'
import { useTracking } from '../../hooks/useTracking'
import DetailCard from '../common/detail-card'
import styled from 'styled-components'
import theme from '../../theme'
import { isSaudiDomestic } from '../../utils/country'

type Props = {}

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-weight: 100;
  font-size: 16px;
  line-height: 21px;
  color: ${theme.midnight800};
`
function CanLeaveAtDoor(_props: Props, ref: React.Ref<HTMLDivElement>) {
  const { track } = useAnalytics()
  const { env } = useEnvVars()
  const { orderDetails, canLeaveAtDoor, hasRobotLeg } = useTracking()

  const [leaveAtDoorToggle, setLeaveAtDoorToggle] = useState(
    (orderDetails?.requiredDocuments ?? []).includes('leave_at_door')
  )

  useEffect(() => {
    setLeaveAtDoorToggle(
      (orderDetails?.requiredDocuments ?? []).includes('leave_at_door')
    )
  }, [orderDetails?.requiredDocuments])

  const handleLeaveAtDoorToggle = async () => {
    try {
      // The route is public and keyed by a sequential order number, so ex-core
      // asks for proof that we hold the order (sc-31822): the order's uuid,
      // which tracking only returns once the customer has matched their phone
      // or opened the uuid link. It goes in the body, not the query string, to
      // keep it out of this request's access log line (a uuid-link session
      // already has it in the page and tracking URLs).
      const response = await ApiClient.put({
        path: `${env.EXCORE_URL}/order_management/${orderDetails?.id}/update_preference`,
        includeAuth: false,
        data: { preference: 'leave_at_door', uuid: orderDetails?.uuid },
      })

      if (!response) throw new Error('not able to update preferences')
      setLeaveAtDoorToggle(!leaveAtDoorToggle)
    } catch (error) {
      notification.error({
        message: 'Unable to update preferences',
        duration: 3,
      })
    }
  }

  if (
    !canLeaveAtDoor ||
    !orderDetails ||
    // Number-only sessions (an SMS link with the plain order number) have no
    // uuid, so no proof to send. The AuthenticateForm card asks for the phone;
    // once it matches, the order comes back with its uuid and the toggle shows.
    !orderDetails.uuid ||
    // A robot hands the parcel to a person with a code; "leave at door" has
    // no meaning for it (sc-29236).
    hasRobotLeg ||
    orderDetails.serviceKind === 'partner_return' ||
    orderDetails.paymentMode === 'paid_on_delivery' ||
    isSaudiDomestic(
      orderDetails.origin?.address?.country,
      orderDetails.destination?.address?.country
    )
  ) {
    return null
  }

  const handleTrackEvent = (event: string) => {
    track(`Website Tracking Page ${EventMapper[event].event}`, {
      action: EventMapper[event].action,
      category: EventMapper[event].category,
      product: 'TrackOrder',
      orderId: String(orderDetails?.id),
    })
  }

  return (
    <DetailCard ref={ref} title={'🚪 Leave at door'}>
      <Content>
        <div>Leave at door</div>
        <div>
          <Switch
            data-testid="leave-at-door-toggle"
            checked={leaveAtDoorToggle}
            onChange={handleLeaveAtDoorToggle}
            onClick={() => handleTrackEvent('leave at door')}
          />
        </div>
      </Content>
    </DetailCard>
  )
}
export default forwardRef(CanLeaveAtDoor)
